import { Game } from 'types/types'
import { Amount } from '@/components/atoms/Amount/Amount'
import { DoubleColor, GetUserLastWinsQuery } from '@/__generated__/graphql'
import { EXPLORERS, SHU_DENOM, USDC_NEW_DENOM } from '@/constants/constants'
import { BigNumberInWei } from '@injectivelabs/utils'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { TokenMoney } from '@ninjablaze/sdk-ts'

export const transformUserLastWin = (address: string, data?: GetUserLastWinsQuery, t?: (key: string) => string) => {
  const tr = (key: string, fallback: string) => (t ? t(key) : fallback)
  if (!data) {
    return null
  }

  const lastJackpotGame = data.jackpotGames?.nodes.at(0)
  const lastDoubleGame = data.doubleUserGames?.nodes.at(0)
  const lastMinesGame = data.minesGames?.nodes.at(0)

  const userJackpotBet = lastJackpotGame?.jackpotBetsByGameId.nodes
    .filter((bet) => bet?.address === address)
    .reduce((prev, curr) => prev.plus(curr?.amount), new BigNumberInWei('0'))

  const lastWin = []

  if (lastMinesGame) {
    lastWin.push({
      game: Game.MINES,
      id: lastMinesGame?.id,
      timestamp: new Date(lastMinesGame.updated).getTime(),
      reward: lastMinesGame.payable,
      denom: lastMinesGame.denom,
      stats: [
        {
          title: tr('common.bet', 'Bet'),
          value: (
            <Amount
              iconSize={'18px'}
              denom={lastMinesGame.denom || 'inj'}
              value={new TokenMoney(lastMinesGame.bet, lastMinesGame.denom || 'inj').humanFormat}
            />
          ),
        },
        {
          title: tr('common.date', 'Date'),
          value: new Date(lastMinesGame.updated).toLocaleString(),
        },
        {
          title: tr('common.txhash', 'TxHash'),
          value: (
            <CopiableLink
              link={`${EXPLORERS.INJECTIVE_TRANSACTIONS}${lastMinesGame.txHash}`}
              value={lastMinesGame.txHash}
            />
          ),
        },
      ],
    })
  }

  if (lastJackpotGame) {
    lastWin.push({
      game: Game.JACKPOT,
      id: lastJackpotGame?.id,
      timestamp: parseInt(lastJackpotGame?.drawTimestamp),
      reward: lastJackpotGame?.pool,
      denom: SHU_DENOM,
      stats: [
        {
          title: tr('common.bet', 'Bet'),
          value: <Amount iconSize={'18px'} value={new TokenMoney(userJackpotBet!.toString(), SHU_DENOM)} />,
        },
        {
          title: tr('common.chance', 'Chance'),
          value: userJackpotBet?.dividedBy(lastJackpotGame?.pool).multipliedBy(100).toFixed(2) + '%',
        },
        {
          title: tr('common.date', 'Date'),
          value: new Date(parseInt(lastJackpotGame?.drawTimestamp)).toLocaleString(),
        },
      ],
    })
  }

  if (lastDoubleGame) {
    lastWin.push({
      game: Game.DOUBLE,
      id: lastDoubleGame?.gameId,
      timestamp: new Date(lastDoubleGame?.timestamp).getTime(),
      denom: lastDoubleGame.denom,
      reward: lastDoubleGame?.totalresult,
      stats: [
        {
          title: tr('common.bet', 'Bet'),
          value: (
            <Amount
              iconSize={'18px'}
              denom={lastDoubleGame.denom || 'inj'}
              value={new TokenMoney(lastDoubleGame.totalamount, lastDoubleGame.denom || 'inj').humanFormat}
            />
          ),
        },
        {
          title: tr('common.date', 'Date'),
          value: new Date(lastDoubleGame?.timestamp).toLocaleString(),
        },
        {
          title: tr('common.color', 'Color'),
          value: lastDoubleGame?.wincolor ? doubleColorMap[lastDoubleGame?.wincolor] : null,
        },
      ],
    })
  }

  return lastWin.sort((a, b) => b.timestamp - a.timestamp).at(0) || null
}

const doubleColorMap: Record<DoubleColor, string> = {
  [DoubleColor.Black]: 'BLUE',
  [DoubleColor.Green]: 'GREEN',
  [DoubleColor.Red]: 'PURPLE',
}
